import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CustomOverlay } from './CustomOverlay';

/**
 * Custom marker component using Google Maps OverlayView API.
 *
 * We use OverlayView instead of AdvancedMarker because:
 * - AdvancedMarker requires a mapId to function
 * - When a mapId is present, custom map styles cannot be applied via the styles prop
 * - We need custom map styles (water color, landscape color, hidden POIs, etc.)
 *
 * This approach:
 * - Works without a mapId
 * - Allows custom styles via the styles prop
 * - Supports React components as marker content via React Portal
 * - Requires extending google.maps.OverlayView class (cannot be avoided)
 */

interface CustomMarkerProps {
    position: { lat: number; lng: number };
    children: ReactNode;
}

export const CustomMarker = ({ position, children }: CustomMarkerProps) => {
    const map = useMap();
    const mapsLibrary = useMapsLibrary('maps');
    const [container, setContainer] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!map || !mapsLibrary) return;

        const latLng = new google.maps.LatLng(position.lat, position.lng);
        const customOverlay = new CustomOverlay(latLng, setContainer);
        customOverlay.setMap(map);

        return () => {
            customOverlay.setMap(null);
            setContainer(null);
        };
    }, [map, mapsLibrary, position.lat, position.lng]);

    return container ? createPortal(children, container) : null;
};
